/**
 * Port Performance Database — Segment filter dropdown.
 * Standardized labels (TCP / new-plan style).
 */

/** Preferred display order / labels for the Segment multi-select. */
export const PORT_DATABASE_TABLE_FILTER_SEGMENTS: readonly string[] = [
  'MR2',
  'LR1 / Panamax',
  'LR2 / Aframax',
  'LR3 / Suezmax',
  'Intermediate / Coastal',
  'Handy(6W)',
  'Handy(9W)',
  'VLCC',
];

type CanonicalSegment = (typeof PORT_DATABASE_TABLE_FILTER_SEGMENTS)[number];

/** Alias groups → canonical standardized label. */
const SEGMENT_ALIAS_GROUPS: ReadonlyArray<{
  label: CanonicalSegment;
  aliases: readonly string[];
}> = [
  {
    label: 'MR2',
    aliases: [
      'mr',
      'mr2',
      'mr 2',
      'm.r',
      'm.r.2',
      'medium range',
      'mediumrange',
    ],
  },
  {
    label: 'LR1 / Panamax',
    aliases: [
      'lr1',
      'panamax',
      'lr1/panamax',
      'lr1 (panamax)',
      'lr1panamax',
      'lr1 / panamax',
    ],
  },
  {
    label: 'LR2 / Aframax',
    aliases: [
      'lr2',
      'aframax',
      'lr2/aframax',
      'lr2 (aframax)',
      'lr2aframax',
      'lr2 / aframax',
    ],
  },
  {
    label: 'LR3 / Suezmax',
    aliases: [
      'lr3',
      'suezmax',
      'lr3/suezmax',
      'lr3 (suezmax)',
      'lr3suezmax',
      'lr3 / suezmax',
    ],
  },
  {
    label: 'Intermediate / Coastal',
    aliases: [
      'intermediate',
      'coastal',
      'intermediate/coastal',
      'intermediate / coastal',
      'intermediate (coastal)',
    ],
  },
  {
    label: 'Handy(6W)',
    aliases: [
      'handy(6w)',
      'handy (6w)',
      'handy(1-6w)',
      'handy (1-6w)',
      'handytankers',
      'handy',
    ],
  },
  {
    label: 'Handy(9W)',
    aliases: [
      'handy(9w)',
      'handy (9w)',
      'handy(1-9w)',
      'handy (1-9w)',
      'handymax',
    ],
  },
  {
    label: 'VLCC',
    aliases: ['vlcc'],
  },
];

/** Normalize for alias lookup: lowercased, no spaces/punctuation. */
export function portDatabaseSegmentDedupKey(value: string): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_/()\\.-]+/g, '');
}

/**
 * Map any API segment spelling to the standardized filter label.
 * Returns trimmed original when no alias matches.
 */
export function canonicalizePortDatabaseSegment(
  value: string | null | undefined,
): string {
  const raw = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (!raw) {
    return '';
  }

  const key = portDatabaseSegmentDedupKey(raw);
  if (!key) {
    return '';
  }

  for (const group of SEGMENT_ALIAS_GROUPS) {
    if (
      group.aliases.some((alias) => portDatabaseSegmentDedupKey(alias) === key)
    ) {
      return group.label;
    }
  }

  // Exact label match (case-insensitive).
  const exactLabel = PORT_DATABASE_TABLE_FILTER_SEGMENTS.find(
    (label) => portDatabaseSegmentDedupKey(label) === key,
  );
  if (exactLabel) {
    return exactLabel;
  }

  return raw.replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Deduplicate a list of segment strings onto standardized labels.
 */
export function dedupePortDatabaseSegments(
  values: Array<string | null | undefined>,
): string[] {
  const preferred = new Set<string>(PORT_DATABASE_TABLE_FILTER_SEGMENTS);
  const seen = new Set<string>();
  const extras: string[] = [];

  for (const value of values || []) {
    const canonical = canonicalizePortDatabaseSegment(value);
    if (!canonical) {
      continue;
    }
    const dedupeKey = portDatabaseSegmentDedupKey(canonical);
    if (!dedupeKey || seen.has(dedupeKey)) {
      continue;
    }
    seen.add(dedupeKey);
    if (!preferred.has(canonical)) {
      extras.push(canonical);
    }
  }

  const orderedPreferred = PORT_DATABASE_TABLE_FILTER_SEGMENTS.filter((label) =>
    seen.has(portDatabaseSegmentDedupKey(label)),
  );
  extras.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return [...orderedPreferred, ...extras];
}

/** Segment filter dropdown options (ALL + standardized segments). */
export function getPortDatabaseTableFilterSegmentOptions(): string[] {
  return ['ALL', ...PORT_DATABASE_TABLE_FILTER_SEGMENTS];
}
